import { useEffect, useMemo, useState } from 'react';
import { homepageApi } from '@mygpt/app/apis/services/homepage';

type BindingListener = (botId: string, templateId: string | null) => void;
const bindingListeners = new Set<BindingListener>();

export function notifyHomepageBindingChanged(botId: string, templateId: string | null) {
  bindingListeners.forEach((listener) => listener(botId, templateId));
}

/** Menu visibility follows the current Bot's saved binding, independently of publication. */
export function useHomepageTemplate(botId?: string) {
  const query = useMemo(() => ({ botId }), [botId]);
  const [binding, setBinding] = useState<{ query: typeof query; templateId: string | null }>();

  useEffect(() => {
    if (!botId) return;
    let active = true;
    let changed = false;
    const listener: BindingListener = (changedBotId, templateId) => {
      if (changedBotId !== botId) return;
      changed = true;
      setBinding({ query, templateId });
    };
    bindingListeners.add(listener);
    homepageApi.get(botId).then(
      ({ data }) => {
        // A late initial response must not overwrite a binding just saved in this tab.
        if (active && !changed)
          setBinding({ query, templateId: data.value.template_id || data.value.draft_template_id || null });
      },
      () => {
        if (active && !changed) setBinding({ query, templateId: null });
      }
    );
    return () => {
      active = false;
      bindingListeners.delete(listener);
    };
  }, [botId, query]);

  // Hide immediately on a Bot switch, before effects or the next request complete.
  return Boolean(botId && binding?.query === query && binding.templateId);
}
